import type React from "react";
import { useTranslation } from "react-i18next";
import { CircleAlert, Copy, LoaderCircle, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Ic } from "./icons";
import { Btn } from "./primitives";
import { Otp } from "./Otp";
import { AppStoreLinks } from "./AppStoreLinks";

/** "JBSWY3DPEHPK3PXP" → "JBSW Y3DP EHPK 3PXP" (the design's manual-key format). */
export function groupSecret(secret: string): string {
  return secret.replace(/\s+/g, "").replace(/(.{4})(?=.)/g, "$1 ");
}

export interface MfaCardProps {
  /**
   * Server-rendered QR SVG markup (`qr_svg` from `POST /mfa/totp/enroll/`,
   * produced by People's own qrcode renderer). Empty while enrolment loads.
   */
  qrSvg: string;
  /** Base32 TOTP secret for manual entry. Empty while enrolment loads. */
  secret: string;
  code: string;
  onCode: (next: string) => void;
  /** Verification error (wrong/expired code), shown under the boxes. */
  error?: string | null;
  /** "Step N" eyebrow; null hides it. Defaults to 2 (after the password card). */
  step?: number | null;
  /** Called when the sixth digit is entered — e.g. to auto-confirm. */
  onComplete?: (code: string) => void;
  disabled?: boolean;
  /**
   * Waiting for a prerequisite (e.g. the account does not exist yet, so there
   * is nothing to enrol): shows this message, a lock instead of the loading
   * spinner, and keeps the code boxes disabled.
   */
  waiting?: React.ReactNode;
}

/**
 * Two-step verification card — authenticator app (TOTP) only. The design's
 * security-key / passkey option is intentionally left out: WeCo only
 * supports TOTP.
 */
export function MfaCard({ qrSvg, secret, code, onCode, error, step = 2, onComplete, disabled, waiting }: MfaCardProps) {
  const { t } = useTranslation();
  const loading = !qrSvg || !secret;
  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(secret.replace(/\s+/g, ""));
      toast.success(t("onboarding.kit.mfa.keyCopied", "Key copied"));
    } catch {
      toast.error(t("onboarding.kit.copyFailed", "Couldn't copy. Select the text and copy it instead."));
    }
  };

  return (
    <section className="ob-glass" style={{ padding: "clamp(20px,3cqi,28px)", display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span className="ob-tile">
          <Ic icon={ShieldCheck} />
        </span>
        <div>
          {step != null && <p className="ob-eye">{t("onboarding.kit.step", { defaultValue: "Step {{n}}", n: step })}</p>}
          <h2 className="ob-h3">{t("onboarding.kit.mfa.title", "Two-step verification")}</h2>
        </div>
      </div>
      {waiting && (
        <p className="ob-sm" data-testid="mfa-waiting" style={{ margin: 0 }}>
          {waiting}
        </p>
      )}
      <div className="mfa-app">
        <div className="mfa-qr" role="img" aria-label={t("onboarding.kit.mfa.qrLabel", "QR code to scan")}>
          {waiting ? (
            <Ic icon={Lock} size={28} color="#64748B" />
          ) : loading ? (
            <Ic icon={LoaderCircle} size={28} className="ob-spin" color="var(--bl)" />
          ) : (
            // Trusted markup: rendered by People's own qrcode (core.services.mfa.qr_svg), never user input.
            <span className="mfa-qr-img" aria-hidden="true" dangerouslySetInnerHTML={{ __html: qrSvg }} />
          )}
        </div>
        <ol
          style={{
            margin: 0,
            paddingInlineStart: 18,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            fontSize: 13.5,
            color: "var(--tx)",
            minWidth: 0,
          }}
        >
          <li>
            {t("onboarding.kit.mfa.step1", "Open any authenticator app on your phone.")}
            <span style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
              <span className="ob-sm">{t("onboarding.kit.mfa.noApp", "Don't have one? Get it free:")}</span>
              <AppStoreLinks />
            </span>
          </li>
          <li>
            {t("onboarding.kit.mfa.step2", "Scan the code, or enter this key:")}
            <span style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
              <code dir="ltr" className="ob-mono mfa-key" data-testid="mfa-secret">
                {secret ? groupSecret(secret) : "···· ···· ···· ····"}
              </code>
              <Btn
                kind="ter"
                size="s"
                icon={Copy}
                disabled={!secret}
                aria-label={t("onboarding.kit.mfa.copyKey", "Copy key")}
                onClick={copyKey}
              />
            </span>
          </li>
          <li>{t("onboarding.kit.mfa.step3", "Enter the 6-digit code it shows.")}</li>
        </ol>
        <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 6 }}>
          <Otp value={code} onChange={onCode} invalid={!!error} disabled={disabled || loading || !!waiting} onComplete={onComplete} />
          {error && (
            <span className="ob-err" role="alert">
              <Ic icon={CircleAlert} weight="bold" />
              {error}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
